import React, { useRef, useState, useEffect, useMemo } from 'react';
import { ownerDocumentOf, ownerWindowOf } from '../../utils/ownerWindow';
import { X, Loader2, ArrowUpDown, FileText } from 'lucide-react';
import { fetchGLTransactions, type GLTransaction } from '../../api';
import { formatCurrency } from '../../utils/formatters';
// eslint-disable-next-line import/no-relative-packages
import { formatCurrency as sharedFormatCurrency } from '../../../../utils/currency';
// eslint-disable-next-line import/no-relative-packages
import { useSourceFileViewer, SourceFileLink } from '../../../../pages/reports/useSourceFileViewer';

interface GLDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  year: number;
  month: number; // 1-12
  /** Last month of a range (1-12); omitted = the single `month`. */
  endMonth?: number;
  columnType: 'Actual' | 'LY';
  currency?: string | null;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

type SortField = 'transactionDate' | 'transactionDesc' | 'vendor' | 'transactionType' | 'transactionAmount';
type SortDirection = 'asc' | 'desc';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  // transactionDate is a calendar date stored at UTC midnight (e.g. 2026-04-01T00:00:00.000Z).
  // Format in UTC so a negative-offset local timezone doesn't shift it back a day (4/1 -> 3/31).
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatAmount(value: number, currency?: string | null): string {
  const absValue = Math.abs(value);
  const formatted = sharedFormatCurrency(absValue, currency);
  if (value < 0) return `(${formatted})`;
  return formatted;
}

export function GLDetailModal({
  isOpen,
  onClose,
  orgId,
  locationId,
  chartOfAccountId,
  accountName,
  accountNumber,
  year,
  month,
  endMonth,
  columnType,
  currency = 'USD',
}: GLDetailModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [transactions, setTransactions] = useState<GLTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('transactionDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [totalRecords, setTotalRecords] = useState(0);
  const { openSourceFile, viewer: sourceFileViewer } = useSourceFileViewer();

  // Determine the actual year to query (LY = previous year)
  const queryYear = columnType === 'LY' ? year - 1 : year;

  // One request per month in the range; the endpoint is month-scoped.
  const lastMonth = Math.max(month, endMonth ?? month);
  const fetchRange = React.useCallback(async () => {
    const months = Array.from({ length: lastMonth - month + 1 }, (_, i) => month + i);
    const pages = await Promise.all(months.map((m) => fetchGLTransactions({ orgId, locationId, chartOfAccountId, year: queryYear, month: m })));
    return {
      data: pages.flatMap((p) => p.data),
      pagination: { totalRecords: pages.reduce((n, p) => n + (p.pagination?.totalRecords || 0), 0) },
    };
  }, [orgId, locationId, chartOfAccountId, queryYear, month, lastMonth]);

  useEffect(() => {
    if (!isOpen) return;

    async function loadTransactions() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchRange();
        setTransactions(response.data);
        setTotalRecords(response.pagination.totalRecords);
      } catch (err) {
        console.error('Failed to load GL transactions:', err);
        setError('Failed to load transactions. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    loadTransactions();
  }, [isOpen, fetchRange]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'transactionDate':
          aVal = a.transactionDate;
          bVal = b.transactionDate;
          break;
        case 'transactionAmount':
          aVal = a.transactionAmount;
          bVal = b.transactionAmount;
          break;
        default:
          aVal = (a[sortField] || '').toString().toLowerCase();
          bVal = (b[sortField] || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transactions, sortField, sortDirection]);

  // Calculate total
  const total = useMemo(() => {
    return transactions.reduce((sum, t) => sum + Number(t.transactionAmount), 0);
  }, [transactions]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const doc = ownerDocumentOf(modalRef.current);
    doc.addEventListener('keydown', handleKeyDown);
    return () => doc.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const monthLabel = lastMonth > month ? `${MONTH_NAMES[month - 1]} – ${MONTH_NAMES[lastMonth - 1]}` : MONTH_NAMES[month - 1];
  const displayLabel = columnType === 'LY'
    ? `${monthLabel} ${queryYear} (Last Year)`
    : `${monthLabel} ${queryYear}`;

  return (
    <div ref={modalRef} className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="fv3-scrim absolute inset-0 bg-black/50" onClick={onClose} />
      {/* Source-invoice viewer — sibling of the backdrop so its clicks can't bubble into onClose */}
      {sourceFileViewer}

      {/* Modal */}
      <div className="fv3-dialog relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="fv3-dialog-title text-lg font-semibold text-slate-900">
              GL Transaction Detail
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-slate-600">{accountNumber}</span>
              <span className="text-slate-300">&middot;</span>
              <span className="text-sm font-medium text-slate-700">{accountName}</span>
              <span className="text-slate-300">&middot;</span>
              <span className="text-sm text-slate-600">{displayLabel}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin mb-3" style={{ color: 'var(--lodge-orange-400)' }} />
              <p className="text-sm text-slate-500">Loading transactions...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  fetchRange()
                    .then(res => { setTransactions(res.data); setTotalRecords(res.pagination.totalRecords); })
                    .catch(() => setError('Failed to load transactions.'))
                    .finally(() => setIsLoading(false));
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 underline"
              >
                Retry
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={40} className="text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">No transactions found for this period.</p>
            </div>
          ) : (
            <table className="fv3-table w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <SortHeader label="Date" field="transactionDate" currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                  <SortHeader label="Description" field="transactionDesc" currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                  <SortHeader label="Vendor" field="vendor" currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                  <SortHeader label="Type" field="transactionType" currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                  <SortHeader label="Amount" field="transactionAmount" currentSort={sortField} direction={sortDirection} onSort={handleSort} align="right" />
                </tr>
              </thead>
              <tbody>
                {sortedTransactions.map((txn) => (
                  <tr key={txn.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">
                      {formatDate(txn.transactionDate)}
                    </td>
                    <td className="py-2 pr-4 text-slate-800 max-w-[250px] truncate" title={txn.transactionDesc || ''}>
                      {txn.sourceDocumentUrl ? (
                        <SourceFileLink
                          title="Open the source invoice"
                          onOpen={() => openSourceFile(txn.sourceDocumentUrl, 'Invoice')}
                        >
                          {txn.transactionDesc || 'Invoice'}
                        </SourceFileLink>
                      ) : (
                        txn.transactionDesc || '—'
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 max-w-[150px] truncate" title={txn.vendor || ''}>
                      {txn.vendor || '—'}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">
                      {txn.transactionType || '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums whitespace-nowrap text-slate-800">
                      {formatAmount(Number(txn.transactionAmount), currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer with total */}
        {!isLoading && !error && transactions.length > 0 && (
          <div className="fv3-dialog-foot fv3-dialog-foot-total px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              {totalRecords} transaction{totalRecords !== 1 ? 's' : ''}
              {totalRecords > 500 && ' (showing first 500)'}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Total:</span>
              <span className="text-sm font-semibold tabular-nums text-slate-900">
                {formatAmount(total, currency)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Sortable column header
function SortHeader({
  label,
  field,
  currentSort,
  direction,
  onSort,
  align = 'left',
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  const isActive = currentSort === field;
  return (
    <th
      className={`py-2 ${align === 'right' ? 'text-right' : 'text-left'} pr-4 text-slate-500 font-medium cursor-pointer hover:text-slate-700 select-none`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          size={12}
          style={{ color: isActive ? 'var(--lodge-orange-400)' : undefined }}
          className={!isActive ? 'text-slate-300' : ''}
        />
        {isActive && (
          <span className="text-[10px]" style={{ color: 'var(--lodge-orange-400)' }}>
            {direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </span>
    </th>
  );
}
